'use client';
/* eslint-disable @next/next/no-img-element */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocale } from 'next-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import { locales, localeNames, defaultLocale, type Locale } from '@/src/i18n/config';
import { getCanonicalPath, getLocalizedPath } from '@/src/i18n/pathnames';
import { getCanonicalCategorySlug, getLocalizedCategorySlug } from '@/src/i18n/categorySlugTranslations';
import { KeyboardArrowDownIcon, CloseIcon } from '../icons';
import { countryFlagSrc } from '@/src/helpers/countries';
import { gatewayClient } from '@/src/api';
import styles from './LanguagePicker.module.css';

const localeToCountryCode: Record<Locale, string> = {
  'en': 'GB',
  'es': 'ES',
  'fr': 'FR',
  'de': 'DE',
  'ro': 'RO',
  'pt': 'PT',
  'zh': 'CN',
  'hi': 'IN',
  'ar': 'SA',
  'bn': 'BD',
  'it': 'IT',
  'ru': 'RU',
};

interface LanguagePickerProps {
  variant?: 'full';
}

export const LanguagePicker: React.FC<LanguagePickerProps> = ({ variant }) => {
  const [open, setOpen] = useState(false);
  const currentLocale = useLocale() as Locale;
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const openModal = useCallback(() => {
    setOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, closeModal]);

  const handleLocaleChange = (newLocale: Locale) => {
    document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=31536000; SameSite=Lax`;
    gatewayClient.auth.updateLocale(newLocale).catch(() => {});
    closeModal();

    const pathSegments = pathname?.split('/').filter(Boolean) || [];
    const currentPathLocale = pathSegments[0] && locales.includes(pathSegments[0] as Locale)
      ? pathSegments[0] as Locale
      : null;

    const pathWithoutLocale = currentPathLocale
      ? '/' + pathSegments.slice(1).join('/') || '/'
      : pathname || '/';

    let canonicalPath = currentPathLocale
      ? getCanonicalPath(pathWithoutLocale, currentPathLocale)
      : pathWithoutLocale;

    // Translate category slugs in the canonical path
    if (canonicalPath.startsWith('/giftcards/')) {
      const segments = canonicalPath.split('/');
      if (segments.length >= 3 && segments[2]) {
        const slugInUrl = segments[2];
        const lookupResult = getCanonicalCategorySlug(slugInUrl, currentPathLocale || defaultLocale);
        segments[2] = lookupResult;
        canonicalPath = segments.join('/');
      }
    }

    // Translate category slugs BEFORE translating the base path
    let pathWithLocalizedCategorySlug = canonicalPath;
    if (canonicalPath.startsWith('/giftcards/')) {
      const segments = canonicalPath.split('/');
      if (segments.length >= 3 && segments[2]) {
        const canonicalSlug = segments[2];
        const localizedSlug = getLocalizedCategorySlug(canonicalSlug, newLocale);
        segments[2] = localizedSlug;
        pathWithLocalizedCategorySlug = segments.join('/');
      }
    }

    // Then translate the base path (e.g., /giftcards -> /carduri-cadou)
    const newLocalizedPath = newLocale === defaultLocale
      ? pathWithLocalizedCategorySlug
      : getLocalizedPath(pathWithLocalizedCategorySlug, newLocale);

    const newPath = newLocale === defaultLocale
      ? newLocalizedPath
      : `/${newLocale}${newLocalizedPath}`;

    const queryString = searchParams?.toString();
    window.location.href = queryString ? `${newPath}?${queryString}` : newPath;
  };

  return (
    <>
      {/* Trigger Button */}
      <div className={`${styles.wrapper}${variant === 'full' ? ` ${styles.full}` : ''}`}>
        <button className={styles.button} onClick={openModal}>
          <span className={styles.flagIcon}>
            <img
              src={countryFlagSrc(localeToCountryCode[currentLocale])}
              alt={localeNames[currentLocale]}
              width={20}
              height={15}
              style={{ width: '20px', height: '15px', borderRadius: '2px' }}
            />
          </span>
          <span className={styles.labelShort}>{currentLocale}</span>
          <span className={styles.labelFull}>{localeNames[currentLocale]}</span>
          <span className={styles.arrowIcon}>
            <KeyboardArrowDownIcon fontSize="small" />
          </span>
        </button>
      </div>

      {/* Modal Popup */}
      {open && (
        <div className={styles.overlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            {/* Header */}
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Language</h3>
              <button className={styles.closeButton} onClick={closeModal} aria-label="Close">
                <CloseIcon fontSize="small" />
              </button>
            </div>

            {/* Language Grid */}
            <div className={styles.languageGrid}>
              {locales.map((locale) => (
                <div
                  key={locale}
                  className={`${styles.languageOption}${locale === currentLocale ? ` ${styles.selected}` : ''}`}
                  onClick={() => handleLocaleChange(locale)}
                >
                  <img
                    src={countryFlagSrc(localeToCountryCode[locale])}
                    alt={localeNames[locale]}
                    width={24}
                    height={18}
                    style={{ width: '24px', height: '18px', borderRadius: '3px' }}
                  />
                  <div className={styles.languageInfo}>
                    <span className={`${styles.languageName}${locale === currentLocale ? ` ${styles.selectedName}` : ''}`}>
                      {localeNames[locale]}
                    </span>
                    <span className={styles.languageCode}>{locale.toUpperCase()}</span>
                  </div>
                  {locale === currentLocale && <div className={styles.checkmark}>✓</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
